/**
 * useMatchFilters Hook
 * 
 * Custom hook for managing filter state and applying filters to match records.
 * Provides memoized filtered results and unique user extraction.
 */

import { useState, useMemo } from 'react';
import type {
  FilterState,
  MatchRecord,
  UniqueUser
} from '../utils/matchFilters';
import {
  DEFAULT_FILTER_STATE,
  applyFilters,
  extractUniqueUsers,
  extractUniqueStatuses,
  hasActiveFilters,
  getActiveFilterCount
} from '../utils/matchFilters';

interface UseMatchFiltersReturn {
  filterState: FilterState;
  setFilterState: React.Dispatch<React.SetStateAction<FilterState>>;
  filteredMatches: MatchRecord[];
  uniqueUsers: UniqueUser[];
  uniqueStatuses: Array<{ value: string; label: string }>;
  clearFilters: () => void;
  togglePanel: () => void;
  hasActiveFilters: boolean;
  activeFilterCount: number;
}

/**
 * Hook for managing match filtering state and logic
 * 
 * @param matches - Array of match records to filter
 * @returns Filter state, filtered matches, and control functions
 */
export function useMatchFilters(matches: MatchRecord[]): UseMatchFiltersReturn {
  const [filterState, setFilterState] = useState<FilterState>(DEFAULT_FILTER_STATE);

  // Memoize filtered matches to prevent unnecessary recalculations
  const filteredMatches = useMemo(() => {
    return applyFilters(matches, filterState);
  }, [matches, filterState]);

  // Memoize unique users to avoid recalculating on every render
  const uniqueUsers = useMemo(() => {
    return extractUniqueUsers(matches);
  }, [matches]);

  // Memoize unique statuses to avoid recalculating on every render
  const uniqueStatuses = useMemo(() => {
    return extractUniqueStatuses(matches);
  }, [matches]);

  // Clear all filters and collapse panel
  const clearFilters = () => {
    setFilterState(DEFAULT_FILTER_STATE);
  };

  // Toggle panel expansion
  const togglePanel = () => {
    setFilterState(prev => ({
      ...prev,
      isPanelExpanded: !prev.isPanelExpanded
    }));
  };

  // Check if any filters are active
  const hasFilters = hasActiveFilters(filterState);

  // Get count of active filters
  const activeCount = getActiveFilterCount(filterState);

  return {
    filterState,
    setFilterState,
    filteredMatches,
    uniqueUsers,
    uniqueStatuses,
    clearFilters,
    togglePanel,
    hasActiveFilters: hasFilters,
    activeFilterCount: activeCount
  };
}
